import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { tokens } from "../../Common/designTokens";

const formatDisplayValue = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") return "---";
  return String(value);
};

const toNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const normalizedValue = value.replace(/,/g, "").trim();
    if (!normalizedValue) return null;
    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }
  return null;
};

const formatQuantityValue = (value) => {
  const numericValue = toNumber(value);
  if (numericValue === null) return formatDisplayValue(value);
  return numericValue.toLocaleString("en-US");
};

const formatPriceValue = (value) => {
  const numericValue = toNumber(value);
  if (numericValue === null) return formatDisplayValue(value);
  return numericValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatYieldValue = (value) => {
  const numericValue = toNumber(value);
  if (numericValue === null) return formatDisplayValue(value);
  return numericValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatCellValue = (key, value) => {
  if (key === "bidQuantity" || key === "offerQuantity") return formatQuantityValue(value);
  if (key === "bidPrice" || key === "offerPrice") return formatPriceValue(value);
  if (key === "bidYield" || key === "offerYield") return formatYieldValue(value);
  return formatDisplayValue(value);
};

const cellStyle = {
  color: tokens.colors.fontColour,
  borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`,
  py: 0.5,
  px: 2,
  fontSize: "14px",
};

const headerCellStyle = {
  ...cellStyle,
  color: tokens.colors.fontColour85,
  fontWeight: 600,
  fontSize: "14px",
  whiteSpace: "nowrap",
};

const depthCellStyle = {
  ...cellStyle,
};

const columns = [
  { label: "Dealer", key: "bidDealer", align: "left" },
  { label: "Bid Yield", key: "bidYield", align: "right" },
  { label: "Bid Qty", key: "bidQuantity", align: "right" },
  { label: "Bid Px", key: "bidPrice", align: "right" },
  { label: "Offer Px", key: "offerPrice", align: "right" },
  { label: "Offer Qty", key: "offerQuantity", align: "right" },
  { label: "Offer Yield", key: "offerYield", align: "right" },
  { label: "Dealer", key: "offerDealer", align: "left" },
];

const MarketDataInfoPanel = ({ marketData, depthData = [], errorMessage, isLoading = false }) => {
  const [expanded, setExpanded] = useState(false);
  const topData = marketData || {};

  const hasDepth = Array.isArray(depthData) && depthData.length > 0;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={{ color: tokens.colors.fontColour, fontWeight: 600, fontSize: "16px", mb: 1, textDecoration: "underline" }}>
        Market Data
      </Typography>
      {isLoading && (
        <Box
          sx={{
            p: 2,
            bgcolor: tokens.colors.defaultGridRowColor,
            borderRadius: "8px",
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <CircularProgress size={20} sx={{ color: tokens.palette.primary }} />
          <Typography sx={{ color: tokens.colors.fontColour85 }}>
            Fetching market data...
          </Typography>
        </Box>
      )}
      {errorMessage && (
        <Box sx={{
          p: 2,
          bgcolor: tokens.colors.defaultGridRowColor,
          borderRadius: "8px",
          border: `1px solid ${tokens.palette.danger}`,
          color: tokens.palette.danger,
        }}>
          <Typography>{errorMessage}</Typography>
        </Box>
      )}
      {!isLoading && !errorMessage && (
      <TableContainer
        sx={{
          bgcolor: tokens.colors.defaultGridRowColor,
          borderRadius: "8px",
          border: `1px solid ${tokens.colors.bodyBorderColor50}`,
        }}
      >
        <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow>
              {hasDepth && (
                <TableCell sx={{ ...headerCellStyle, width: 40, px: 0.5 }} />
              )}
              {columns.map((col) => (
                <TableCell key={col.key} sx={{ ...headerCellStyle, textAlign: col.align }}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Top-of-book row */}
            <TableRow
              sx={{
                cursor: hasDepth ? "pointer" : "default",
                "&:hover": hasDepth ? { bgcolor: `${tokens.colors.bodyBorderColor50}30` } : {},
              }}
              onClick={() => hasDepth && setExpanded((prev) => !prev)}
            >
              {hasDepth && (
                <TableCell sx={{ ...cellStyle, width: 40, px: 0.5, textAlign: "center" }}>
                  <IconButton size="small" sx={{ color: tokens.colors.fontColour, p: 0 }}>
                    {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>
                </TableCell>
              )}
              {columns.map((col) => (
                <TableCell key={col.key} sx={{ ...cellStyle, textAlign: col.align }}>
                  {formatCellValue(col.key, topData[col.key])}
                </TableCell>
              ))}
            </TableRow>

            {/* Depth rows - collapsible */}
            {hasDepth && (
              <TableRow>
                <TableCell
                  sx={{ p: 0, borderBottom: "none" }}
                  colSpan={columns.length + 1}
                >
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                      <TableBody>
                        {depthData.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell sx={{ ...depthCellStyle, width: 40, px: 0.5 }} />
                            {columns.map((col) => (
                              <TableCell key={col.key} sx={{ ...depthCellStyle, textAlign: col.align }}>
                                {formatCellValue(col.key, row[col.key])}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Collapse>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}
    </Box>
  );
};

export default MarketDataInfoPanel;
